import { useCallback, useEffect, useState } from "react";

import { useAuthContext } from "@/components/AuthProvider";
import Box from "@/components/Box";
import OtpSetup from "@/components/otp/OtpSetup";
import ProfileSectionPanel, { ProfileWhiteCard } from "@/components/otp/ProfileSectionPanel";
import { fetchOtpDevices, fetchOtpSetup } from "@/hooks/useOtpApi";
import Badge from "@/tpo/Badge";
import ChevronComponent from "@/tpo/Chevron";
import Spacer from "@/tpo/Spacer";
import Stack from "@/tpo/Stack";
import ButtonV2 from "@/v2/Buttons";

function SectionHeader({ title, description }) {
  return (
    <Box>
      <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]} lineHeight="130%">
        {title}
      </Box>
      {description ? (
        <>
          <Spacer py={[2, 2, 20]} />
          <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="130%">
            {description}
          </Box>
        </>
      ) : null}
    </Box>
  );
}

function formatDeviceName(name) {
  if (name === "App TOTP") {
    return "Authenticator app";
  }
  return name;
}

function formatOtpDate(isoString) {
  if (!isoString) {
    return "—";
  }

  return new Date(isoString).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric"
  });
}

function CurrentDevice({ device, loading, error }) {
  if (loading && !device) {
    return (
      <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} color="carbon">
        Loading authenticator details…
      </Box>
    );
  }

  if (error) {
    return (
      <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} color="red">
        {error}
      </Box>
    );
  }

  if (!device) {
    return null;
  }

  return (
    <Box border="1px solid" borderColor="haze" borderRadius={5} px={16} py={14}>
      <Box fontFamily="gilroyBold" fontSize={[16, 16, 18]} lineHeight="130%">
        {formatDeviceName(device.name)}
      </Box>
      <Stack gap={4} mt={8}>
        <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="130%" color="carbon">
          Added on {formatOtpDate(device.createdAt)}
        </Box>
        <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="130%" color="carbon">
          Last used {formatOtpDate(device.lastUsedAt)}
        </Box>
      </Stack>
    </Box>
  );
}

function TwoFactorAuthContent({ otp, showSetup, setShowSetup, refreshAuth, csrfToken }) {
  const [device, setDevice] = useState(null);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [deviceError, setDeviceError] = useState("");
  const [setupPrefetching, setSetupPrefetching] = useState(false);
  const [setupQrDataUri, setSetupQrDataUri] = useState("");
  const [setupError, setSetupError] = useState("");
  const replacing = Boolean(otp?.hasDevice);

  const loadDevice = useCallback(
    async ({ silent = false } = {}) => {
      if (!otp?.hasDevice) {
        setDevice(null);
        setDeviceError("");
        return;
      }

      if (!silent) {
        setDeviceLoading(true);
      }
      setDeviceError("");

      try {
        const data = await fetchOtpDevices(csrfToken);
        setDevice(data.devices?.[0] || null);
      } catch (loadError) {
        if (!silent) {
          setDevice(null);
        }
        setDeviceError(loadError.message);
      } finally {
        setDeviceLoading(false);
      }
    },
    [csrfToken, otp?.hasDevice]
  );

  useEffect(() => {
    loadDevice();
  }, [loadDevice]);

  const handleOpenSetup = useCallback(async () => {
    if (replacing) {
      setSetupError("");
      setShowSetup(true);
      return;
    }

    setSetupPrefetching(true);
    setSetupError("");

    try {
      const data = await fetchOtpSetup(csrfToken);
      if (!data.qrDataUri) {
        throw new Error("Could not load QR code.");
      }
      setSetupQrDataUri(data.qrDataUri);
      setShowSetup(true);
    } catch (openError) {
      setSetupError(openError.message);
    } finally {
      setSetupPrefetching(false);
    }
  }, [csrfToken, replacing, setShowSetup]);

  const handleCancelSetup = useCallback(() => {
    setShowSetup(false);
    setSetupQrDataUri("");
    setSetupError("");
  }, [setShowSetup]);

  return (
    <>
      <SectionHeader
        title="Two-factor authentication"
        description={
          showSetup && !replacing
            ? "Scan the QR code with your authenticator app, then enter the verification code below."
            : otp?.hasDevice
            ? "Your account is protected with an authenticator app. You will be asked for a code when signing in."
            : "Add an extra layer of security by linking an authenticator app to your account."
        }
      />

      <Box display={showSetup ? "none" : "block"}>
        {otp?.hasDevice ? (
          <Badge bg="green" color="white" size="sm">
            Enabled
          </Badge>
        ) : null}

        <CurrentDevice device={device} loading={deviceLoading} error={deviceError} />

        <Box mt={otp?.hasDevice || device || deviceLoading || deviceError ? 20 : 0}>
          {setupError ? (
            <Box color="red" fontSize={14} mb={12}>
              {setupError}
            </Box>
          ) : null}
          <ButtonV2
            color="green"
            type="button"
            rightIcon={<ChevronComponent />}
            size={["sm", "sm", "md"]}
            disabled={setupPrefetching}
            onClick={handleOpenSetup}
          >
            {setupPrefetching
              ? "Loading…"
              : otp?.hasDevice
              ? "Change authenticator"
              : "Enable two-factor authentication"}
          </ButtonV2>
        </Box>
      </Box>

      {showSetup && (setupQrDataUri || replacing) ? (
        <OtpSetup
          key={setupQrDataUri || "replacing"}
          embedded
          replacing={replacing}
          initialQrDataUri={setupQrDataUri || undefined}
          onComplete={async () => {
            await refreshAuth();
            setShowSetup(false);
            setSetupQrDataUri("");
            await loadDevice({ silent: true });
          }}
          onCancel={handleCancelSetup}
        />
      ) : null}
    </>
  );
}

export default function TwoFactorAuthSection({ otp, embedded = false }) {
  const { csrfToken, refreshAuth } = useAuthContext();
  const [showSetup, setShowSetup] = useState(false);

  const content = (
    <TwoFactorAuthContent
      otp={otp}
      showSetup={showSetup}
      setShowSetup={setShowSetup}
      refreshAuth={refreshAuth}
      csrfToken={csrfToken}
    />
  );

  if (embedded) {
    return <ProfileWhiteCard gap={20}>{content}</ProfileWhiteCard>;
  }

  return <ProfileSectionPanel gap={20}>{content}</ProfileSectionPanel>;
}
